import { useState } from "react";
import { Link, Navigate, Route, Routes } from "react-router-dom";
import { AccountChip } from "../components/AccountChip";
import { Frame } from "../components/Frame";
import { NavExit, NavItems, NavOffsite } from "../components/Nav";
import { Screen } from "../components/Screen";
import { usePanel } from "../panelContext";
import {
  ACCOUNT_SECTION,
  CONNECTED_APPS,
  HOUSEHOLDS_SECTION,
  PERSON_SECTIONS,
  PERSON_SECTIONS_HEADING,
} from "../sections";
import { ConnectedApps } from "./ConnectedApps";
import { Households } from "./Households";

/**
 * `/me` — the person scope (ADR 0021 decision 2).
 *
 * **A person is not a tenant**, and the split is spatial as well as structural (#293's
 * wireframes): the sidebar becomes *You*, the header stays exactly as it was, and a route back
 * to the family sits at the top. Nothing here is scoped to a family and nothing here may
 * invent one — the same rule the server's `/app/me` routes are held to.
 *
 * Three things live here: the account, which is #104's other half; the households somebody
 * belongs to and the way to start another, which is #360; and connected apps, which is #116.
 */
export function PersonScope() {
  const { families, email, signOut } = usePanel();
  // Somewhere to go back to. A member with no open family cannot reach this scope at all —
  // the shell shows them the server's guidance instead — so the fallback is for the one
  // family whose id is not in the URL yet, never for a member of none.
  const home = families[0] === undefined ? "/" : `/families/${families[0].familyId}`;

  return (
    <Frame
      scope={<span className="scope__only">You</span>}
      account={<AccountChip email={email} onSignOut={signOut} />}
      nav={
        <>
          <NavExit>
            <Link className="nav__back" to={home}>
              ← Back to {families[0]?.name ?? "your family"}
            </Link>
          </NavExit>
          <NavItems base="/me" heading={PERSON_SECTIONS_HEADING} sections={PERSON_SECTIONS} />
          <NavOffsite />
        </>
      }
    >
      <Routes>
        <Route index element={<Navigate to={ACCOUNT_SECTION.path} replace />} />
        <Route
          path={ACCOUNT_SECTION.path}
          element={<Account email={email} onSignOut={signOut} />}
        />
        <Route path={HOUSEHOLDS_SECTION.path} element={<Households />} />
        <Route path={CONNECTED_APPS.path} element={<ConnectedApps />} />
        <Route path="*" element={<Navigate to={ACCOUNT_SECTION.path} replace />} />
      </Routes>
    </Frame>
  );
}

/**
 * Naming the account, and leaving it.
 *
 * **A second sign-out, and deliberately so.** The account menu carries one because that is
 * where somebody who noticed the wrong name in the header will look; this page carries one
 * because it is the *page about the account*, and because at phone widths it is a full-width
 * target rather than an item in a popover. They are the same call.
 *
 * **It ends the session for the whole browser and the copy says so**, because it cannot do
 * anything else: the Stytch session is one cookie on `.iamboone.app`, shared by every tab and
 * every first-party host, so this also ends an `/authorize` flow open elsewhere. `/onboard`
 * makes the same statement in the same words for the same reason (#104).
 *
 * **Nothing navigates afterwards.** `endSession` clears the local session, the SDK's
 * subscription fires, and the shell's own session gate renders the signed-out card — one place
 * decides what a browser with no session looks like, rather than this button guessing.
 */
function Account({ email, onSignOut }: { email: string | null; onSignOut: () => void }) {
  const [signingOut, setSigningOut] = useState(false);

  return (
    <Screen title={ACCOUNT_SECTION.label} blurb={ACCOUNT_SECTION.blurb}>
      <p className="identity">
        <span className="identity__label">Signed in as</span>
        <span className="identity__value">{email ?? "an account in this browser"}</span>
      </p>
      <button
        className="cta"
        type="button"
        disabled={signingOut}
        onClick={() => {
          // The flag is never cleared: `endSession` uses `forceClear`, so the session is gone
          // either way and the shell replaces this screen. Re-enabling the button would flash
          // "Sign out" back on a page that is about to disappear.
          setSigningOut(true);
          onSignOut();
        }}
      >
        {signingOut ? "Signing out…" : "Sign out"}
      </button>
      <p className="footnote">
        Signing out signs this browser out of Boone completely, including any other Boone tab you
        have open. It doesn't disconnect Claude or ChatGPT, and it changes nothing Boone has saved
        for your family.
      </p>
    </Screen>
  );
}
